import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Favorecido, FavorecidoEnquadramento, FavorecidoService, FavorecidoTipo, FuncaoService, GlobalService, Login, Proponente, SituacaoProponente } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { FavorecidoTipoService } from '../service/favorecido-tipo.service';

declare var $: any;

@Component({
  selector: 'lib-favorecido-cadastro-dlg',
  templateUrl: './favorecido-cadastro-dlg.component.html'
})
export class FavorecidoCadastroDlgComponent implements OnInit, OnChanges {

  public listaTipos: FavorecidoTipo[];
  public listaEnquadramento: { id: FavorecidoEnquadramento, nome: string }[];

  // dlg padrão
  @Input() public login: Login;
  @Input() public entidade: Favorecido;
  @Output() callback: EventEmitter<Favorecido> = new EventEmitter();

  // dlg para proponentes
  public proponenteEpp: boolean;
  public proponenteSituacao: SituacaoProponente;
  public proponenteMotivo: string;
  public listaSituacoes: { id: SituacaoProponente, nome: string }[];
  @Input() public origem_proponente: boolean = false;
  @Input() public proponente: Proponente;
  @Output() callbackProponente: EventEmitter<Proponente> = new EventEmitter();

  public entidadeForm: FormGroup;
  protected router: Router;
  protected activatedRoute: ActivatedRoute;
  protected fb: FormBuilder;
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected injector: Injector,
    protected funcaoService: FuncaoService,
    protected globalService: GlobalService,
    protected favorecidoTipoService: FavorecidoTipoService,
    private favorecidoService: FavorecidoService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes['entidade']) {
        this.entidadeForm.patchValue(this.entidade);
      } else if (changes['proponente']) {
        this.limparProponente();
        if (this.proponente) {
          if (this.entidadeForm) {
            if (this.proponente.favorecido.id) {
              this.entidadeForm.patchValue(this.proponente.favorecido);
            } else {
              this.criarCamposForm();
            }
            this.buscar();
          }
          this.proponenteEpp = this.proponente.epp_me;
          this.proponenteSituacao = this.proponente.situacao;
          this.proponenteMotivo = this.proponente.motivo;
        }
      }
    }
  }

  ngOnInit(): void {
    this.router = this.injector.get(Router);
    this.fb = this.injector.get(FormBuilder);
    this.activatedRoute = this.injector.get(ActivatedRoute);
    this.criarCamposForm();

    this.favorecidoTipoService.filtrar(1, -1,
      { 'cidade.id': this.login.cidade.id, orderBy: 'id' })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaTipos = res ? res.content : new Array<FavorecidoTipo>();
      });

    this.listaEnquadramento = this.globalService.obterListaEnquadramentoFornecedores();
    this.listaSituacoes = this.globalService.obterSituacoesProponente();
    this.limparProponente();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public submitForm() {
    if (this.entidadeForm.invalid) {
      toastr.error('Há campos obrigatórios não informados!');
      return;
    }

    if (!this.funcaoService.validarCNPJ(this.entidadeForm.get('cpf_cnpj').value)) {
      toastr.warning('O CNPJ informado está inválido!')
      return;
    }

    if (this.origem_proponente) {
      if (!this.proponenteSituacao) this.proponenteSituacao = 'HABILITADO';
      else if (this.proponenteSituacao !== 'HABILITADO' && !this.proponenteMotivo) {
        toastr.error('Informe o motivo para a situação do proponente!');
        return;
      }
    }

    this.salvar();
  }

  private salvar() {
    const entidade: Favorecido = Favorecido.converteJson(this.entidadeForm.value);
    if (entidade.id) {
      this.favorecidoService.atualizar(entidade).pipe(takeUntil(this.unsubscribe))
        .subscribe(res => {
          this.acaoSucesso(res);
          this.criarCamposForm();
        }, error => this.acaoErro(error));
    } else {
      this.favorecidoService.inserir(entidade).pipe(takeUntil(this.unsubscribe))
        .subscribe(res => {
          this.acaoSucesso(res);
          this.criarCamposForm();
        }, error => this.acaoErro(error));
    }
  }

  private acaoSucesso(entidade: Favorecido) {
    toastr.success('Registro salvo com sucesso!');
    if (this.origem_proponente) {
      const prop = new Proponente();
      prop.epp_me = this.proponenteEpp;
      prop.situacao = this.proponenteSituacao;
      prop.motivo = this.proponenteMotivo;
      prop.favorecido = entidade;
      this.callbackProponente.emit(prop);
    } else {
      this.callback.emit(entidade);
    }
    this.fechar();
  }

  private acaoErro(error: any) {
    toastr.options.timeOut = 10000;
    toastr.options.closeButton = true;
    toastr.options.tapToDismiss = false;
    if (error.error && error.error.payload) {
      toastr.error(error.error.payload);
    } else {
      toastr.error('Ocorreu um erro ao processar a sua solicitação');
    }
  }

  private criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      nome: [null, [Validators.required]],
      cpf_cnpj: [null, [Validators.required]],
      nome_fantasia: ['-', [Validators.required]],
      responsavel: [null, [Validators.required]],
      cep: ['-', [Validators.required]],
      endereco: ['-', [Validators.required]],
      num_endereco: ['-', [Validators.required]],
      bairro: ['-', [Validators.required]],
      municipio: ['-', [Validators.required]],
      uf: ['-', [Validators.required]],
      ddd_fone: [null],
      telefone: [null, [Validators.required]],
      pis: [null],
      email: [null, [Validators.required]],
      url: [null],
      enquadramento: [null, [Validators.required]],
      autarquia: [false, [Validators.required]],
      autorizado: [false, [Validators.required]],
      servidor: [false, [Validators.required]],
      simples_nacional: [false, [Validators.required]],
      gasto_fixo: [false, [Validators.required]],
      terceiro_setor: [false, [Validators.required]],
      aux: [null],
      tipo: [null, [Validators.required]],
      cidade: [this.login.cidade, [Validators.required]],
    });
  }

  compareFn(c1: any, c2: any): boolean {
    if (c1 && c2) {
      if (c1.id && c2.id) {
        return c1.id === c2.id;
      } else if (c1.chave && c2.chave) {
        return c1.chave == c2.chave;
      } else {
        return c1 === c2;
      }
    } else {
      return false;
    }
  }

  public buscar() {
    const cpf_cnpj: string = this.entidadeForm.get('cpf_cnpj').value;
    if (cpf_cnpj) {
      this.criarCamposForm();
      this.entidadeForm.get('cpf_cnpj').setValue(cpf_cnpj);
      if (!this.funcaoService.validarCNPJ(cpf_cnpj)) {
        toastr.warning('O CNPJ informado está inválido!')
        return;
      }
      this.favorecidoService.obter({ cpf_cnpj, cidade_id: this.login.cidade.id, relations: 'cidade,tipo' })
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res: Favorecido) => {
          if (res) {
            this.entidadeForm.patchValue(res);
            this.verificarEPP();
          }
        });
    }
  }

  public abrirConsultaFavorecido() {
    $('#dialogFavorecidoBusca').modal('show');
  }

  public callbackFavorecido(fav: Favorecido) {
    if (fav) {
      this.entidadeForm.get('cpf_cnpj').setValue(fav.cpf_cnpj);
      this.buscar();
    }
  }

  public verificarEPP() {
    const enquadramento: FavorecidoEnquadramento = this.entidadeForm.get('enquadramento').value;
    if (enquadramento && this.origem_proponente) {
      this.proponenteEpp = (enquadramento === 'EPP' || enquadramento === 'ME')
    }
  }

  private limparProponente() {
    this.proponenteEpp = false;
    this.proponenteSituacao = 'HABILITADO';
    this.proponenteSituacao = undefined;
  }

  public fechar() {
    // this.limparProponente();
    // this.criarCamposForm();
    $('#dialogFavorecidoCadastro').modal('hide');
  }
}
